/**
 * Session Detail Page
 * 
 * Displays session metadata, chat history, and actions (Resume, Delete).
 * Route: /sessions/:sessionId
 */
import { useState, useRef, useEffect } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Play, Trash2, RefreshCw, AlertCircle, ChevronRight, Loader2, Square, ArrowDown, Search, Compass, FileText, CheckCircle2, Wrench, Bot, ArrowRight, XCircle, MoreVertical, ChevronLeft, FastForward, Eye } from 'lucide-react'
import { PageShell } from '@/components/common/PageShell'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { ScrollArea } from '@/components/ui/scroll-area'
import { SessionChatView, SessionChatViewRef } from '@/components/sessions/SessionChatView'
import { SessionExpiryWarning } from '@/components/sessions/SessionExpiryWarning'
import { SessionContextSidebar } from '@/components/sessions/SessionContextSidebar'
import { SessionStatusBadge } from '@/components/sessions/SessionStatusBadge'
import { AgentIndicator } from '@/components/sessions/AgentIndicator'
import { SupervisorPanel } from '@/components/sessions/SupervisorPanel'
import { ProjectBadge } from '@/components/projects/ProjectBadge'
import { ProjectArchivedBanner } from '@/components/projects/ProjectArchivedBanner'
import { ConstraintsInput } from '@/components/forms/ConstraintsInput'
import { ConfirmDialog } from '@/components/common/ConfirmDialog'
import { useSessionWithPolling, useDeleteSession, useSessionTokens, useCancelSession } from '@/api/orchestrator'
import { useProject } from '@/api/projects'
import { useToast } from '@/hooks/use-toast'
import { useSignalR } from '@/hooks/useSignalR'
import { usePermissions } from '@/hooks/usePermissions'
import { isSignalREnabled } from '@/lib/signalRService'
import type { SessionEvent } from '@/lib/types'
import { cn } from '@/lib/utils'

/**
 * Get icon for session event type
 */
function getEventIcon(eventType: string) {
  switch (eventType) {
    case 'agent_started':
      return <Bot className="w-4 h-4" />
    case 'progress_update':
      return <ArrowRight className="w-4 h-4" />
    case 'tool_called':
      return <Wrench className="w-4 h-4" />
    case 'sub_agent_message':
      return <Bot className="w-4 h-4" />
    case 'complete':
      return <CheckCircle2 className="w-4 h-4" />
    case 'error':
      return <XCircle className="w-4 h-4" />
    case 'discovery_started':
      return <Search className="w-4 h-4" />
    case 'discovery_skipped':
      return <FastForward className="w-4 h-4" />
    case 'scout_iteration':
      return <Compass className="w-4 h-4" />
    case 'evidence_found':
      return <FileText className="w-4 h-4" />
    case 'discovery_complete':
      return <CheckCircle2 className="w-4 h-4" />
    default:
      return <ArrowRight className="w-4 h-4" />
  }
}

/**
 * Extract agent name from event data if available
 */
function getEventAgentName(event: SessionEvent): string | null {
  if (event.data && typeof event.data === 'object') {
    const agentName = event.data.agentName || event.data.AgentName
    if (typeof agentName === 'string') {
      return agentName
    }
  }
  return null
}

/** Messages fetched per page of the session transcript. */
const MESSAGE_PAGE_SIZE = 200

export function SessionDetailPage() {
  const { sessionId } = useParams<{ sessionId: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [isCancelling, setIsCancelling] = useState(false)
  const [selectedEvent, setSelectedEvent] = useState<SessionEvent | null>(null)
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false)
  const chatViewRef = useRef<SessionChatViewRef>(null)
  const eventsEndRef = useRef<HTMLDivElement>(null)
  const useSignalRFlag = isSignalREnabled()

  // Permission checks
  const { hasPermission } = usePermissions()
  const canStopSession = hasPermission('session:stop')
  const canResumeSession = hasPermission('session:resume')
  const canDeleteSession = hasPermission('session:delete')

  // Supervisor event tracking
  const [policyDecisions, setPolicyDecisions] = useState<any[]>([])
  const [retryAttempts, setRetryAttempts] = useState<any[]>([])
  const [guardianIterations, setGuardianIterations] = useState<any[]>([])
  const [subTasks, setSubTasks] = useState<any[]>([])
  const [currentIteration, setCurrentIteration] = useState<number>()
  const [maxIterations, setMaxIterations] = useState<number>()

  // Fetch session details with polling for active sessions (fallback when SignalR is disabled).
  // Only the newest messages are polled: a PR review's transcript runs to thousands of messages, and
  // re-reading all of them every few seconds is what the orchestrator and MongoDB pay for, per open tab.
  // "Show earlier messages" widens the window; polling keeps it anchored to the newest message.
  const [messageLimit, setMessageLimit] = useState(MESSAGE_PAGE_SIZE)
  const {
    data: session,
    isLoading,
    error: fetchError,
    isError,
    dataUpdatedAt,
    isFetching,
    refetch,
  } = useSessionWithPolling(sessionId, { limit: messageLimit })

  // SignalR real-time updates (when enabled)
  const { connectionState } = useSignalR({
    sessionId,
    enabled: useSignalRFlag && !!sessionId,
    onSessionStatusChanged: (payload) => {
      console.log('Session status changed:', payload)
      // Refetch session data to get updated status
      refetch()
    },
    onProgressUpdate: (payload) => {
      console.log('Progress update:', payload)
      // Refetch to get updated messages/events
      refetch()
    },
    onSessionComplete: (payload) => {
      console.log('Session complete:', payload)
      refetch()
      toast({
        title: 'Session Complete',
        description: 'The session has finished successfully.',
      })
    },
    onSessionError: (payload) => {
      console.log('Session error:', payload)
      refetch()
      toast({
        title: 'Session Error',
        description: payload.error,
        variant: 'destructive',
      })
    },
    onPolicyDecision: (payload) => {
      console.log('Policy decision:', payload)
      setPolicyDecisions(prev => [...prev, payload])
    },
    onRetryAttempt: (payload) => {
      console.log('Retry attempt:', payload)
      setRetryAttempts(prev => [...prev, payload])
    },
    onGuardianIteration: (payload) => {
      console.log('Guardian iteration:', payload)
      setGuardianIterations(prev => [...prev, payload])
      setCurrentIteration(payload.iteration)
      setMaxIterations(payload.maxIterations)
    },
    onSubTaskCompleted: (payload) => {
      console.log('Sub-task completed:', payload)
      setSubTasks(prev => [...prev, payload])
    },
  })

  // Fetch project details to show constraints (if session has a project)
  const { data: project } = useProject(session?.metadata.projectId)

  // Fetch token usage for this session
  const { 
    data: tokenUsage, 
    isLoading: isLoadingTokens 
  } = useSessionTokens(sessionId)

  // Delete mutation
  const deleteSession = useDeleteSession()

  // Cancel mutation
  const cancelSession = useCancelSession()

  // Scroll to latest event when events are first loaded
  useEffect(() => {
    if (session?.events && session.events.length > 0 && eventsEndRef.current) {
      // Small delay to ensure the ScrollArea has rendered
      setTimeout(() => {
        eventsEndRef.current?.scrollIntoView({ behavior: 'instant' })
      }, 100)
    }
  }, [session?.events?.length]) // Only trigger when events count changes (initial load)

  const handleCancelSession = async () => {
    if (!sessionId) return
    setIsCancelling(true)
    try {
      const result = await cancelSession.mutateAsync(sessionId)
      if (result.success) {
        toast({
          title: 'Session stopped',
          description: result.message,
        })
      } else {
        // Session was already in a terminal state
        toast({
          title: 'Cannot stop session',
          description: result.message,
          variant: 'destructive',
        })
      }
    } catch (error) {
      console.error('Failed to cancel session:', error)
      toast({
        title: 'Error stopping session',
        description: error instanceof Error ? error.message : 'An unexpected error occurred',
        variant: 'destructive',
      })
    } finally {
      setIsCancelling(false)
    }
  }

  const handleDeleteClick = () => {
    setShowDeleteDialog(true)
    setDeleteError(null)
  }

  const handleDeleteConfirm = async () => {
    if (!sessionId) return

    try {
      await deleteSession.mutateAsync(sessionId)
      setShowDeleteDialog(false)
      navigate('/sessions')
    } catch (error) {
      setDeleteError(
        error instanceof Error
          ? error.message
          : 'Failed to delete session'
      )
    }
  }

  // Loading state
  if (isLoading) {
    return (
      <PageShell>
        <div className="p-6 space-y-6" role="status" data-testid="loading-skeleton">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-[400px] w-full" />
        </div>
      </PageShell>
    )
  }

  // 404 state
  if (isError && fetchError) {
    const is404 = fetchError instanceof Error && 
      (fetchError.message.includes('404') || fetchError.message.includes('not found'))
    
    return (
      <PageShell>
        <div className="p-6 flex flex-col items-center justify-center py-16 space-y-6">
          <Alert variant="destructive" className="max-w-md">
            <AlertTitle>
              {is404 ? 'Session Not Found' : 'Error Loading Session'}
            </AlertTitle>
            <AlertDescription>
              {is404 
                ? `The session "${sessionId}" could not be found. It may have expired or been deleted.`
                : fetchError.message
              }
            </AlertDescription>
          </Alert>
          <Link to="/sessions">
            <Button variant="outline">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Sessions
            </Button>
          </Link>
        </div>
      </PageShell>
    )
  }

  // No session data
  if (!session) {
    return null
  }

  const { metadata, messages, events, hasMore, totalCount } = session

  // Calculate if session is expiring (TTL < 2 hours)
  const isExpiring = metadata.timeToLive !== undefined && 
    metadata.timeToLive !== null && 
    metadata.timeToLive < 7200

  // Check if session is actively polling (Running or Queued)
  const isActiveSession = metadata.status === 'Running' || metadata.status === 'Queued' || metadata.status === 'WaitingForInput'

  // Format last updated time
  const formatLastUpdated = () => {
    if (!dataUpdatedAt) return null
    const date = new Date(dataUpdatedAt)
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    })
  }

  return (
    <PageShell>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 flex-wrap">
              {metadata.status && (
                <SessionStatusBadge status={metadata.status} size="sm" />
              )}
              <h1 className="text-lg font-semibold">Session {metadata.sessionId}</h1>
              <span className="text-xs text-muted-foreground">{metadata.messageCount} messages</span>
              {isActiveSession && (
                <span className="text-sm text-muted-foreground flex items-center gap-1">
                  {isFetching && <RefreshCw className="h-3 w-3 animate-spin" />}
                  {useSignalRFlag && (
                    <span className="flex items-center gap-1">
                      <span className={cn(
                        "h-2 w-2 rounded-full",
                        connectionState === 'connected' && "bg-green-500",
                        connectionState === 'connecting' && "bg-yellow-500 animate-pulse",
                        connectionState === 'reconnecting' && "bg-yellow-500 animate-pulse",
                        connectionState === 'disconnected' && "bg-red-500"
                      )} />
                      {connectionState === 'connected' && 'Live'}
                      {connectionState === 'connecting' && 'Connecting...'}
                      {connectionState === 'reconnecting' && 'Reconnecting...'}
                      {connectionState === 'disconnected' && 'Disconnected'}
                    </span>
                  )}
                  {!useSignalRFlag && `Last updated: ${formatLastUpdated()}`}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {metadata.projectName && metadata.projectId && (
                <ProjectBadge
                  projectName={metadata.projectName}
                  projectId={metadata.projectId}
                  isArchived={metadata.isProjectArchived}
                  size="md"
                />
              )}
              <Button variant="outline" size="sm" asChild>
                <Link to={`/sessions/${sessionId}/context`}>
                  <Eye className="mr-2 h-4 w-4" />
                  Context
                </Link>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  console.log('Scroll button clicked, ref:', chatViewRef.current)
                  chatViewRef.current?.scrollToBottom()
                }}
                title="Scroll to latest message"
              >
                <ArrowDown className="h-4 w-4" />
              </Button>
              <Link to="/sessions">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to Sessions
                </Button>
              </Link>
              {metadata.status === 'Running' || metadata.status === 'Queued' ? (
                canStopSession && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCancelSession}
                    disabled={isCancelling}
                  >
                    {isCancelling ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Square className="mr-2 h-4 w-4" />
                    )}
                    Stop Session
                  </Button>
                )
              ) : (
                canResumeSession && (
                  <Button size="sm" asChild>
                    <Link to={`/orchestrate/${sessionId}`}>
                      <Play className="mr-2 h-4 w-4" />
                      Resume
                    </Link>
                  </Button>
                )
              )}
              {canDeleteSession && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleDeleteClick}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </Button>
              )}
            </div>
          </div>

          {/* Inline warnings */}
          {metadata.isProjectArchived && metadata.projectName && (
            <ProjectArchivedBanner
              projectName={metadata.projectName}
              variant="warning"
            />
          )}
          {isExpiring && metadata.expiresAt && (
            <SessionExpiryWarning expiresAt={metadata.expiresAt} />
          )}
          {metadata.status === 'Failed' && metadata.errorMessage && (
            <Alert variant="destructive" data-testid="session-error-alert">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Session Failed</AlertTitle>
              <AlertDescription className="space-y-2">
                <p>{metadata.errorMessage}</p>
                {metadata.errorDetails && (
                  <p className="text-sm text-muted-foreground">{metadata.errorDetails}</p>
                )}
              </AlertDescription>
            </Alert>
          )}
        </div>

        {/* Three-column layout — viewport-height minus header offset */}
        <div className="flex gap-4 h-[calc(100vh-13rem)]">
          {/* Left Sidebar: Events & Constraints */}
          <div className={cn(
            "hidden xl:block shrink-0 transition-all duration-300 overflow-y-auto",
            isSidebarCollapsed ? "w-12" : "w-[280px]"
          )}>
            {isSidebarCollapsed && (
              <Button
                variant="ghost"
                size="sm"
                className="w-full h-10 justify-center"
                onClick={() => setIsSidebarCollapsed(false)}
                aria-label="Expand sidebar"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            )}

            {!isSidebarCollapsed && (
              <div className="space-y-4">
                {/* Execution Events */}
                {events && events.length > 0 && (
                  <Card>
                    <CardHeader className="pb-3 flex flex-row items-center justify-between">
                      <CardTitle className="text-sm">Execution Events</CardTitle>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 -mr-2"
                        onClick={() => setIsSidebarCollapsed(true)}
                        aria-label="Collapse sidebar"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <ScrollArea className="max-h-[60vh]">
                        <ul className="space-y-1 pr-3">
                          {events.map((event, index) => {
                            const agentName = getEventAgentName(event)
                            return (
                              <li
                                key={`${event.timestamp}-${index}`}
                                className="flex flex-col gap-1 py-1.5 px-2 rounded hover:bg-muted/50 transition-colors group"
                              >
                                <div className="flex items-center justify-between gap-2">
                                  <div className="flex items-center gap-2 min-w-0 flex-1">
                                    <div className="flex-shrink-0">
                                      {getEventIcon(event.eventType)}
                                    </div>
                                    <span className="text-xs truncate" title={event.eventType}>
                                      {event.eventType}
                                    </span>
                                  </div>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                                    onClick={() => setSelectedEvent(event)}
                                  >
                                    <MoreVertical className="h-3.5 w-3.5" />
                                  </Button>
                                </div>
                                {agentName && (
                                  <div className="pl-6">
                                    <AgentIndicator agentName={agentName} className="text-[10px] px-1.5 py-0" />
                                  </div>
                                )}
                              </li>
                            )
                          })}
                          {/* Scroll anchor for auto-scroll to latest event */}
                          <div ref={eventsEndRef} />
                        </ul>
                      </ScrollArea>
                    </CardContent>
                  </Card>
                )}

                {/* Project Constraints */}
                {project && project.constraints && project.constraints.length > 0 && (
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm">Project Constraints</CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <ScrollArea className="max-h-48">
                        <ConstraintsInput
                          value={project.constraints}
                          onChange={() => {}}
                          readOnly
                        />
                      </ScrollArea>
                    </CardContent>
                  </Card>
                )}

                {/* Supervisor Panel */}
                {useSignalRFlag && (
                  <SupervisorPanel
                    policyDecisions={policyDecisions}
                    retryAttempts={retryAttempts}
                    guardianIterations={guardianIterations}
                    subTasks={subTasks}
                    currentIteration={currentIteration}
                    maxIterations={maxIterations}
                  />
                )}
              </div>
            )}
          </div>

          {/* Chat View */}
          <div className="flex-1 min-w-0 border rounded-xl overflow-hidden bg-card">
            {hasMore && (
              <div className="flex items-center justify-center gap-2 border-b px-3 py-2 text-xs text-muted-foreground">
                <span>
                  Showing the latest {messages.length} of {totalCount ?? messages.length} messages
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={isFetching}
                  onClick={() => setMessageLimit((limit) => limit + MESSAGE_PAGE_SIZE)}
                >
                  Show earlier messages
                </Button>
              </div>
            )}
            <SessionChatView
              ref={chatViewRef}
              messages={messages ?? []} 
              scrollToBottomOnLoad={true}
              sessionId={sessionId}
            />
          </div>

          {/* Right Sidebar: Context */}
          <div className="hidden lg:block w-80 shrink-0 overflow-y-auto">
            <SessionContextSidebar
              session={metadata}
              tokenUsage={tokenUsage}
              isLoadingTokens={isLoadingTokens}
            />
          </div>
        </div>
      </div>

      {/* Event Details Dialog */}
      <Dialog open={!!selectedEvent} onOpenChange={(open) => !open && setSelectedEvent(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedEvent && getEventIcon(selectedEvent.eventType)}
              {selectedEvent?.eventType}
            </DialogTitle>
          </DialogHeader>
          {selectedEvent && (
            <div className="space-y-3">
              <div>
                <div className="text-sm font-medium text-muted-foreground mb-1">Timestamp</div>
                <div className="text-sm">{new Date(selectedEvent.timestamp).toLocaleString()}</div>
              </div>
              {selectedEvent.message && (
                <div>
                  <div className="text-sm font-medium text-muted-foreground mb-1">Message</div>
                  <ScrollArea className="max-h-96">
                    <div className="text-sm whitespace-pre-wrap p-3 bg-muted rounded-md">
                      {selectedEvent.message}
                    </div>
                  </ScrollArea>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        title="Delete Session"
        description={`Are you sure you want to delete session "${sessionId}"? This action cannot be undone.${deleteError ? ` Error: ${deleteError}` : ''}`}
        confirmLabel="Delete"
        onConfirm={handleDeleteConfirm}
        variant="destructive"
        isLoading={deleteSession.isPending}
      />
    </PageShell>
  )
}
